import { usePermissions } from '@church/data'
import { createFileRoute, redirect } from '@tanstack/react-router'
import {
  DepositsPanel,
  FundsPanel,
  GivingSettingsPanel,
  StatementsPanel,
} from '../components/giving/AdminPanels.tsx'
import { DashboardPanel } from '../components/giving/DashboardPanel.tsx'
import { BatchesPanel, DonationsPanel } from '../components/giving/LedgerPanels.tsx'
import { InKindPanel, RecurringElsewherePanel } from '../components/giving/RecordsPanels.tsx'
import { PageHeader, Tabs } from '../components/ui.tsx'
import { useTabParam } from '../lib/tabs.ts'

/**
 * Giving. Behind `giving:read`, which no preset but owner and finance holds —
 * an admin who runs the church's software is not thereby entitled to see what
 * each family gives. The server refuses regardless; this redirect is manners.
 */
export const Route = createFileRoute('/_authed/_shell/giving')({
  beforeLoad: ({ context }) => {
    if (!context.me.grants.permissions.includes('giving:read')) throw redirect({ to: '/' })
  },
  component: GivingScreen,
})

// Most-used first: on a phone the row scrolls, and what is out of sight
// should be what a treasurer opens once a month.
const TABS = [
  'Dashboard',
  'Donations',
  'Batches',
  'Deposits',
  'Funds & campaigns',
  'Statements',
  'In-kind',
  'Recurring elsewhere',
  'Settings',
] as const
type Tab = (typeof TABS)[number]

function GivingScreen() {
  const { can } = usePermissions()
  const tabs = can('giving:write') ? TABS : TABS.filter((entry) => entry !== 'Batches')
  const [tab, setTab] = useTabParam<Tab>(tabs)

  return (
    <div className="space-y-6">
      <PageHeader title="Giving" />
      <Tabs tabs={tabs} value={tab} onChange={setTab}>
        {tab === 'Dashboard' && <DashboardPanel />}
        {tab === 'Donations' && <DonationsPanel />}
        {tab === 'Batches' && <BatchesPanel />}
        {tab === 'Deposits' && <DepositsPanel />}
        {tab === 'In-kind' && <InKindPanel />}
        {tab === 'Recurring elsewhere' && <RecurringElsewherePanel />}
        {tab === 'Funds & campaigns' && <FundsPanel />}
        {tab === 'Statements' && <StatementsPanel />}
        {tab === 'Settings' && <GivingSettingsPanel />}
      </Tabs>
    </div>
  )
}
